import React, { useState, useEffect } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';

import messages from './messages';
import styles from './styles.module.less';
import Global from '../../../../../store/global-selectors';
import { fetchReturnLocations, setDefaultReturnLocation } from '../../../../../store/returns/returns.api';
import { refreshStoreList } from '../../../../../store/seller/seller.actions';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { ActivationModalContent, ActivationModalFooter } from '../modal/components';
import { ENHANCED_RETURNS_MODAL_STORE_SETUP_COMPLETE_PATH } from '../modal/enhanced-returns-activation-step-detective';

const ReturnLocationSetup = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const { storeGuid } = useParams();
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [isError, setIsError] = useAsyncSafeState(false);
  const [error, setError] = useAsyncSafeState(null);
  const [shipFroms, setShipFroms] = useAsyncSafeState(null);
  const noShipFromAddresses = shipFroms?.length === 0;
  const [selectedShipFromId, setSelectedShipFromId] = useState(null);
  const disabled = isLoading || !selectedShipFromId || shipFroms?.length === 0;
  const trackActivateEnhancedReturns = useTrackWorkflow(Workflows.ACTIVATE_ENHANCED_RETURNS_FLOW);
  const shipstationShipFromLocations = useSelector(Global.selectSellerShipFroms);
  const hasShipstationShipFromLocations = shipstationShipFromLocations?.length > 0;

  // Handle radio button change
  const handleShipFromChange = (event) => setSelectedShipFromId(event.target.value);
  const onClickNoLocationsLink = () => history.push('/settings/warehouses');

  useEffect(() => {
    /**
     * Fetch ship from addresses from SSR if they haven't loaded
     * and if we have verified that Shipstation has at least one
     * location
     *
     * TODO: Handle paging
     */
    if (!shipFroms && hasShipstationShipFromLocations) {
      try {
        setIsLoading(true);
        setIsError(false);
        setError(null);
        fetchReturnLocations(storeGuid)
          .then((result) => {
            setShipFroms(result);
            setSelectedShipFromId(result?.find((shipFrom) => shipFrom.isDefault)?.locationIdExternal);

            // set the first ship from as the default if no default is found
            if (!selectedShipFromId && result?.length > 0) {
              setSelectedShipFromId(result[0].locationIdExternal);
            }
          })
          .catch((err) => {
            setError(err);
            setIsError(true);
          })
          .finally(() => setIsLoading(false));
      } catch (err) {
        setIsError(true);
        trackActivateEnhancedReturns(WorkflowSteps.ERROR, {
          error: err,
        });
        setIsLoading(false);
        setError(intl.formatMessage(messages.shippingLocationFetchFailureMessage));
      }
    }
  }, [
    setError,
    setIsError,
    selectedShipFromId,
    setIsLoading,
    setShipFroms,
    shipFroms,
    storeGuid,
    intl,
    trackActivateEnhancedReturns,
    hasShipstationShipFromLocations,
  ]);

  const completeSetup = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setIsError(false);

      // Call the API to set the default return location
      await setDefaultReturnLocation(storeGuid, selectedShipFromId);
      trackActivateEnhancedReturns(WorkflowSteps.SELECT_RETURN_LOCATION, {
        selectedShipFromId,
      });

      dispatch(refreshStoreList());

      // Navigate to store setup complete page
      history.push(ENHANCED_RETURNS_MODAL_STORE_SETUP_COMPLETE_PATH.replace(':storeGuid', storeGuid));
    } catch (err) {
      setIsError(true);
      setError(intl.formatMessage(messages.shippingLocationSelectionFailureMessage));
      trackActivateEnhancedReturns(WorkflowSteps.ERROR, {
        error: err,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onClickSecondaryButton = () => history.goBack();

  if (isLoading) return <LoadingIndicator data-testid='loading-indicator' />;

  return (
    <>
      <ActivationModalContent>
        <Text textType={TextType.H3} value={messages.returnLocationHeading} />
        <Text className={styles.returnLocationDescription} textType={TextType.H5} value={messages.returnLocationDescription} />

        {isError ? (
          <BannerMessageV2
            className={styles.returnLocationBanner}
            content={<Text textType={TextType.Paragraph} value={error} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Error}
          />
        ) : null}

        {noShipFromAddresses ? (
          <BannerMessageV2
            className={styles.returnLocationBanner}
            content={<Text textType={TextType.Paragraph} value={messages.noShippingLocations} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Error}
          />
        ) : null}

        {!hasShipstationShipFromLocations && (
          <BannerMessageV2
            className={styles.returnLocationBanner}
            content={
              <Text
                className={styles.noShipstationLocations}
                formattedMessageValues={{
                  a: (chunks) => (
                    <Button onClick={onClickNoLocationsLink} theme='link'>
                      {chunks}
                    </Button>
                  ),
                }}
                textType={TextType.Paragraph}
                value={messages.noShipstationLocations}
              />
            }
            layout={BannerLayout.Full}
            status={BannerStatus.Warning}
          />
        )}

        {shipFroms?.length > 0 && (
          <div className={styles.shipFromList}>
            <ul className={styles.radioList}>
              {shipFroms?.map((shipFrom) => (
                <li key={shipFrom.locationIdExternal} className={styles.radioItem}>
                  <RadioCheckListItem
                    className={styles.radioCheckItem}
                    isChecked={selectedShipFromId === shipFrom.locationIdExternal}
                    name='shipFrom'
                    onChecked={handleShipFromChange}
                    value={shipFrom.locationIdExternal}
                  >
                    {/* TODO: This will likely be reworked with updated styles and ui-components if needed. */}
                    <div className={styles.shipFromDetails}>
                      <Text className={styles.returnLocationName} textType={TextType.H5} value={shipFrom.name} />
                      <div className={styles.address}>
                        {shipFrom?.line1 ? <Text textType={TextType.Caption} value={shipFrom.line1} /> : null}
                        {shipFrom?.line2 ? <Text textType={TextType.Caption} value={shipFrom.line2} /> : null}
                        {shipFrom?.city ? <Text textType={TextType.Caption} value={shipFrom.city} /> : null}
                        {shipFrom?.state ? <Text textType={TextType.Caption} value={shipFrom.state} /> : null}
                        {shipFrom?.postalCode ? <Text textType={TextType.Caption} value={shipFrom.postalCode} /> : null}
                        {shipFrom?.countryCode ? <Text textType={TextType.Caption} value={shipFrom.countryCode} /> : null}
                      </div>
                    </div>
                  </RadioCheckListItem>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ActivationModalContent>

      <ActivationModalFooter
        disabled={disabled}
        onClickBackButton={() => history.goBack()}
        onClickPrimaryButton={completeSetup}
        onClickSecondaryButton={onClickSecondaryButton}
        primaryText={messages.finishButton}
        secondaryText={messages.cancelButton}
        showBackButton={false}
      />
    </>
  );
};

export default ReturnLocationSetup;
